/** Canonical slot value + three-way Foxline/SoftOne classifier. */

export const SLOT_CLASS = Object.freeze({
  IDLE: "idle",
  IN_SYNC: "in_sync",
  API_ELIGIBLE: "api_eligible",
  MANUAL_CHANGE: "manual_change",
  DELETED_IN_SOFTONE: "deleted_in_softone",
  FOXLINE_CHANGED: "foxline_changed",
  CONFLICT: "conflict",
  LOCAL_MATCHES_FOXLINE: "local_matches_foxline",
  PREEXISTING: "preexisting",
});

export const SLOT_LOG_SOURCE = Object.freeze({
  LOCAL: "local",
  UPLOAD: "upload",
  FOXLINE: "foxline",
});

export const SLOT_LOG_EVENT = Object.freeze({
  ASSIGNED: "assigned",
  REMOVED: "removed",
  MODIFIED: "modified",
  FIRST_SEND: "first_send",
  API_BLANK_UPLOAD: "api_blank_upload",
  MARK_SUBMITTED: "mark_submitted",
  RESTORE_VIA_API: "restore_via_api",
  REPLACE_VIA_API: "replace_via_api",
  ACCEPT_DELETION: "accept_deletion",
  FOXLINE_CHANGED: "foxline_changed",
  FOXLINE_DELETED: "foxline_deleted",
  UPLOAD_FAILED: "upload_failed",
});

const NON_SENDABLE = new Set(["ΑΔΕΙΑ", "ΑΝΑΡΡΩΤΙΚΗ", "OTHER_STORE"]);

/** SoftOne webservice user id (Foxline WEB / REFID). */
export const SOFTONE_WEB_USER_ID = "263";

export function isSoftoneWebUser(userId) {
  const digits = String(userId ?? "").replace(/\D/g, "");
  return digits === SOFTONE_WEB_USER_ID;
}

/** Normalize API ``action_audit`` / daily-report row for UI flags. */
export function normalizeActionAudit(raw) {
  if (!raw || typeof raw !== "object") return null;
  const insuser = String(raw.insuser ?? raw.INSUSER ?? "").trim();
  const upduser = String(raw.upduser ?? raw.UPDUSER ?? "").trim();
  const desktop =
    raw.desktop_edited != null
      ? Boolean(raw.desktop_edited)
      : Boolean(upduser) && !isSoftoneWebUser(upduser);
  return {
    employee_code: employeeCodeDigits(raw.employee_code || raw.CODE || ""),
    ref_date: String(raw.ref_date || raw.DAYSTART || raw.date || "").slice(0, 10),
    insuser: insuser || null,
    insusername: String(raw.insusername || raw.INSUSERNAME || raw.INUSERNAME || "").trim() || null,
    insdate: String(raw.insdate || raw.INSDATE || "").trim() || null,
    upduser: upduser || null,
    updusername: String(raw.updusername || raw.UPDUSERNAME || "").trim() || null,
    upddate: String(raw.upddate || raw.UPDDATE || "").trim() || null,
    web_created:
      raw.web_created != null ? Boolean(raw.web_created) : (insuser ? isSoftoneWebUser(insuser) : false),
    web_last_touch:
      raw.web_last_touch != null
        ? Boolean(raw.web_last_touch)
        : (upduser ? isSoftoneWebUser(upduser) : false),
    desktop_edited: desktop,
  };
}

/**
 * Filled monitor slots whose CODE+date has no daily-report audit yet.
 * Used to nudge "Fetch SoftOne actions".
 */
export function slotsMissingActionAudit(monitorSlots, actionsAvailable) {
  if (!actionsAvailable) {
    return filledWorkMonitorSlots(monitorSlots);
  }
  return filledWorkMonitorSlots(monitorSlots).filter((s) => {
    const audit = normalizeActionAudit(s.action_audit || s.actionAudit);
    return !audit;
  });
}

export function actionsFetchNudgeNeeded(monitor, actions) {
  if (!monitor?.available) return false;
  if (!actions?.available && !actions?.fetched_at) {
    return filledWorkMonitorSlots(monitor.slots).length > 0;
  }
  return slotsMissingActionAudit(monitor.slots, true).length > 0;
}

/** True when getDailyReport says last SoftOne touch was not WEB/Ops (263). */
export function slotNotTouchedByOps(slot) {
  const audit = normalizeActionAudit(slot?.action_audit || slot?.actionAudit);
  if (!audit) return false;
  return Boolean(audit.desktop_edited);
}

export function actionAuditShortLabel(slot, lang) {
  const audit = normalizeActionAudit(slot?.action_audit || slot?.actionAudit);
  if (!audit) return "";
  const gr = lang === "gr";
  if (audit.desktop_edited) {
    const who = audit.updusername || audit.upduser || (gr ? "SoftOne" : "SoftOne");
    return gr ? `SoftOne (όχι Ops): ${who}` : `SoftOne (not Ops): ${who}`;
  }
  if (audit.web_last_touch) {
    return gr ? "Ops (WEB)" : "Ops (WEB)";
  }
  return "";
}

export function slotKey(employeeCode, dateIso) {
  return `${String(employeeCode || "").trim()}::${String(dateIso || "").slice(0, 10)}`;
}

export function parseSlotKey(key) {
  const raw = String(key || "");
  const idx = raw.lastIndexOf("::");
  if (idx < 0) return { employee_code: raw, date: "" };
  return { employee_code: raw.slice(0, idx), date: raw.slice(idx + 2) };
}

/**
 * AFM digits for SoftOne↔Foxline joins.
 * SoftOne PCODE often drops leading zeros that Foxline CODE keeps; strip them for matching only.
 */
export function employeeCodeDigits(code) {
  const raw = String(code || "").replace(/\D/g, "");
  if (!raw) return "";
  const stripped = raw.replace(/^0+/, "");
  return stripped || raw;
}

/** Attach daily-report actions onto monitor rows (for SoftOne-owned lock detection). */
export function attachActionAuditToMonitorSlots(monitorSlots, actions) {
  const byKey = {};
  (actions || []).forEach((a) => {
    const code = employeeCodeDigits(a.employee_code || a.CODE);
    const date = String(a.ref_date || a.DAYSTART || a.date || "").slice(0, 10);
    if (!code || !date) return;
    byKey[`${code}::${date}`] = a;
  });
  return (monitorSlots || []).map((row) => {
    const code = employeeCodeDigits(row.employee_code || row.employeeCode || row.PCODE);
    const date = String(row.date || row.work_date || "").slice(0, 10);
    const audit = code && date ? byKey[`${code}::${date}`] : null;
    if (!audit) return row;
    return {
      ...row,
      action_audit: normalizeActionAudit(audit) || row.action_audit || row.actionAudit || null,
    };
  });
}

export function digitSlotKey(employeeCode, dateIso) {
  const dig = employeeCodeDigits(employeeCode);
  return dig ? slotKey(dig, dateIso) : slotKey(employeeCode, dateIso);
}

/** Find a classified slot by exact key, then by digit-normalized employee code. */
export function lookupSlotByKey(slotByKey, employeeCode, dateIso) {
  if (!slotByKey) return null;
  const exact = slotKey(employeeCode, dateIso);
  if (slotByKey[exact]) return slotByKey[exact];
  const dig = employeeCodeDigits(employeeCode);
  if (!dig) return null;
  const date = String(dateIso || "").slice(0, 10);
  if (!date) return null;
  const want = slotKey(dig, date);
  if (slotByKey[want]) return slotByKey[want];
  for (const slot of Object.values(slotByKey)) {
    if (!slot) continue;
    if (String(slot.date || "").slice(0, 10) !== date) continue;
    if (employeeCodeDigits(slot.employee_code) === dig) return slot;
  }
  return null;
}

export function isEmptySlot(value) {
  return canonicalSlotValue(value) === "";
}

export function isSoftoneSendableValue(value) {
  const v = canonicalSlotValue(value);
  if (!v || NON_SENDABLE.has(v)) return false;
  if (v === "OFF" || v === "NO_WORK") return true;
  return /^\d{2}:\d{2}-\d{2}:\d{2}$/.test(v);
}

/** HR / super-admin can push when Internal differs from SoftOne and local is sendable. */
export function slotNeedsSoftonePush(slot) {
  if (!slot || slotSoftoneChangeLocked(slot)) return false;
  const local = canonicalSlotValue(slot.local);
  const fox = canonicalSlotValue(slot.foxline);
  if (!isSoftoneSendableValue(local)) return false;
  return local !== fox;
}

export function slotHasSoftoneActionId(slot) {
  const id = String(slot?.softone_id || slot?.softoneId || "").trim();
  return Boolean(id) && !["0", "none", "null"].includes(id.toLowerCase());
}

/**
 * SoftOne owns this day — Ops must not edit Internal; Internal must be cleared.
 * - SoftOne filled and no document KEY (cannot cancel/replace), or
 * - SoftOne desktop last touch (not Ops WEB), or
 * - SoftOne changed away from Ops baseline (FOXLINE_CHANGED / CONFLICT).
 * Not MANUAL_CHANGE (Ops edited Internal; SoftOne still matches last submit).
 */
export function slotSoftoneChangeLocked(slot) {
  if (!slot || isEmptySlot(slot.foxline)) return false;
  if (!slotHasSoftoneActionId(slot)) return true;
  if (slotNotTouchedByOps(slot)) return true;
  const c = slot.classification;
  return c === SLOT_CLASS.FOXLINE_CHANGED || c === SLOT_CLASS.CONFLICT;
}

export function softoneChangeLockLabel(lang) {
  return lang === "gr"
    ? "Κλειδωμένο από SoftOne"
    : "Locked from SoftOne";
}

/** Full explanation shown in the locked-slot dialog (EN / GR). */
export function softoneChangeLockExplanation(lang) {
  return lang === "gr"
    ? "Αυτή η βάρδια ανήκει στο SoftOne (άλλαξε από SoftOne ή δεν υπάρχει action id για αντικατάσταση). Το Ops καθάρισε το εσωτερικό slot και δεν επεξεργάζεται εδώ. Αλλάξτε ή διαγράψτε τη στο SoftOne· όταν γίνει κενή (hole), το Ops ξαναπαίρνει τον έλεγχο."
    : "SoftOne owns this slot (changed in SoftOne UI, or Ops has no action id to replace it). Ops cleared the Internal slot and will not edit it here. Change or delete it in SoftOne; when SoftOne clears it (a hole), Ops can schedule this day again.";
}

/** Occupied SoftOne cells that need cancel+resend (not blank upload). */
export function slotNeedsSoftoneResend(slot) {
  return slotNeedsSoftonePush(slot) && !isEmptySlot(slot.foxline) && slotHasSoftoneActionId(slot);
}

export function slotsNeedingSoftoneResend(slots) {
  return (slots || []).filter(slotNeedsSoftoneResend);
}

export function slotsNeedingSoftonePush(slots) {
  return (slots || []).filter(slotNeedsSoftonePush);
}

function looksLikeNoWorkCode(raw) {
  const compact = String(raw || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[\s_-]/g, "");
  return compact.includes("μηεργασ") || compact.includes("no_work") || compact.includes("nowork");
}

export function canonicalSlotValue(raw) {
  if (raw == null) return "";
  const s = String(raw).trim();
  if (!s) return "";
  const upper = s.toUpperCase();
  if (upper === "OFF") return "OFF";
  if (upper === "NO_WORK" || looksLikeNoWorkCode(s)) return "NO_WORK";
  if (s === "ΑΔΕΙΑ" || s === "ΑΝΑΡΡΩΤΙΚΗ" || upper === "OTHER_STORE") {
    return s === "ΑΔΕΙΑ" || s === "ΑΝΑΡΡΩΤΙΚΗ" ? s : "OTHER_STORE";
  }
  const m = s.match(/^(\d{1,2}):(\d{2})\s*[-–]\s*(\d{1,2}):(\d{2})$/);
  if (m) {
    return `${m[1].padStart(2, "0")}:${m[2]}-${m[3].padStart(2, "0")}:${m[4]}`;
  }
  return s;
}

export function rotaRowToCanonical(row) {
  if (!row) return "";
  const status = String(row.status || "").toLowerCase();
  if (status === "leave") return "ΑΔΕΙΑ";
  if (status === "sick") return "ΑΝΑΡΡΩΤΙΚΗ";
  if (status === "other_store") return "OTHER_STORE";
  if (status === "no_work") return "NO_WORK";
  if (row.is_off) return "OFF";
  const start = String(row.shift_start || row.from_hour || "").slice(0, 5);
  const end = String(row.shift_end || row.to_hour || "").slice(0, 5);
  if (start && end) return canonicalSlotValue(`${start}-${end}`);
  if (row.shift_value) return canonicalSlotValue(row.shift_value);
  return "";
}

export function foxlineMonitorRowToCanonical(row) {
  if (!row) return "";
  if (row.empty === true || row.is_empty === true) return "";
  const noWork =
    row.no_work_day === true ||
    row.noworkday === true ||
    Number(row.series) === 4007 ||
    looksLikeNoWorkCode(row.work_code || row.sacode || row.value);
  if (noWork) return "NO_WORK";
  if (row.day_off === true || Number(row.series) === 4006) return "OFF";
  const start = String(row.from_hour || row.shift_start || "").slice(0, 5);
  const end = String(row.to_hour || row.shift_end || "").slice(0, 5);
  if (start && end) return canonicalSlotValue(`${start}-${end}`);
  if (row.value != null) return canonicalSlotValue(row.value);
  return "";
}

/**
 * Three-way slot state.
 * submitted = last API send or HR mark-submitted
 * foxline   = last fetched work monitor
 * local     = current tool value
 */
export function classifySlot({ submitted, foxline, local } = {}) {
  const sub = canonicalSlotValue(submitted);
  const fox = canonicalSlotValue(foxline);
  const loc = canonicalSlotValue(local);
  const subEmpty = isEmptySlot(sub);
  const foxEmpty = isEmptySlot(fox);
  const locEmpty = isEmptySlot(loc);
  const locSendable = isSoftoneSendableValue(loc);

  if (subEmpty && foxEmpty && locEmpty) return SLOT_CLASS.IDLE;

  if (subEmpty) {
    if (!foxEmpty) return SLOT_CLASS.PREEXISTING;
    if (locSendable) return SLOT_CLASS.API_ELIGIBLE;
    return SLOT_CLASS.IDLE;
  }

  if (fox === sub && loc === sub) return SLOT_CLASS.IN_SYNC;
  if (fox === sub && loc !== sub) return SLOT_CLASS.MANUAL_CHANGE;
  if (foxEmpty && loc === sub) return SLOT_CLASS.DELETED_IN_SOFTONE;
  if (foxEmpty && loc !== sub) {
    if (locSendable) return SLOT_CLASS.API_ELIGIBLE;
    return locEmpty ? SLOT_CLASS.DELETED_IN_SOFTONE : SLOT_CLASS.MANUAL_CHANGE;
  }
  if (fox !== sub && loc === sub) return SLOT_CLASS.FOXLINE_CHANGED;
  if (fox !== sub && loc === fox) return SLOT_CLASS.LOCAL_MATCHES_FOXLINE;
  return SLOT_CLASS.CONFLICT;
}

export function slotClassTone(classification) {
  switch (classification) {
    case SLOT_CLASS.API_ELIGIBLE:
      return { color: "#15803D", bg: "#DCFCE7", border: "#86EFAC" };
    case SLOT_CLASS.MANUAL_CHANGE:
      return { color: "#92400E", bg: "#FEF3C7", border: "#F59E0B" };
    case SLOT_CLASS.DELETED_IN_SOFTONE:
      return { color: "#9A3412", bg: "#FFEDD5", border: "#FB923C" };
    case SLOT_CLASS.FOXLINE_CHANGED:
      return { color: "#9A3412", bg: "#FFEDD5", border: "#EA580C" };
    case SLOT_CLASS.CONFLICT:
      return { color: "#991B1B", bg: "#FEE2E2", border: "#EF4444" };
    case SLOT_CLASS.PREEXISTING:
      return { color: "#5B21B6", bg: "#EDE9FE", border: "#8B5CF6" };
    case SLOT_CLASS.LOCAL_MATCHES_FOXLINE:
      return { color: "#1E40AF", bg: "#DBEAFE", border: "#3B82F6" };
    case SLOT_CLASS.IN_SYNC:
      return { color: "#065F46", bg: "#D1FAE5", border: "#34D399" };
    default:
      return { color: "var(--text-muted)", bg: "transparent", border: "var(--border)" };
  }
}

export function slotClassLabel(classification, lang) {
  const gr = lang === "gr";
  switch (classification) {
    case SLOT_CLASS.API_ELIGIBLE:
      return gr ? "Επιλέξιμο για αποστολή (κενό SoftOne)" : "Eligible for API upload (SoftOne blank)";
    case SLOT_CLASS.MANUAL_CHANGE:
      return gr ? "Εσωτερικό ≠ SoftOne (αποθηκεύστε Εσωτερικό, μετά SoftOne)" : "Internal ≠ SoftOne (save Internal, then SoftOne)";
    case SLOT_CLASS.DELETED_IN_SOFTONE:
      return gr ? "Διαγράφηκε στο SoftOne" : "Deleted in SoftOne";
    case SLOT_CLASS.FOXLINE_CHANGED:
      return gr ? "Άλλαξε στο SoftOne — το Εσωτερικό δείχνει την παλιά τιμή" : "Changed in SoftOne — Internal still shows the original";
    case SLOT_CLASS.CONFLICT:
      return gr ? "Σύγκρουση: Εσωτερικό ≠ αποστολή ≠ SoftOne" : "Conflict: Internal ≠ submitted ≠ SoftOne";
    case SLOT_CLASS.PREEXISTING:
      return gr ? "Υπάρχει ήδη στο SoftOne (δεν στάλθηκε από εμάς)" : "Already in SoftOne (not sent by us)";
    case SLOT_CLASS.LOCAL_MATCHES_FOXLINE:
      return gr ? "SoftOne και Εσωτερικό συμφωνούν — εκκρεμεί σήμανση" : "SoftOne and Internal agree — mark submitted";
    case SLOT_CLASS.IN_SYNC:
      return gr ? "Συγχρονισμένο" : "In sync";
    default:
      return gr ? "Χωρίς εκκρεμότητα" : "No action";
  }
}

export function displaySlotValue(value, lang) {
  const v = canonicalSlotValue(value);
  if (!v) return lang === "gr" ? "(κενό)" : "(blank)";
  if (v === "OFF") return lang === "gr" ? "Ρεπό" : "Rest day";
  if (v === "NO_WORK") return lang === "gr" ? "Μη εργασία" : "No Work";
  if (v === "ΑΔΕΙΑ") return lang === "gr" ? "Άδεια" : "Leave";
  if (v === "ΑΝΑΡΡΩΤΙΚΗ") return lang === "gr" ? "Αναρρωτική" : "Sick leave";
  if (v === "OTHER_STORE") return lang === "gr" ? "Άλλο κατ." : "Other store";
  return v;
}

export function isoToDayKey3(iso) {
  const parts = String(iso || "").slice(0, 10).split("-").map(Number);
  if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) return "";
  const d = new Date(parts[0], parts[1] - 1, parts[2]);
  return ["sun", "mon", "tue", "wed", "thu", "fri", "sat"][d.getDay()] || "";
}

export function scheduleGridToSlotMap(schedule, weekDates, dayKeyFn, toIsoFn) {
  const map = {};
  const dates = weekDates || [];
  Object.entries(schedule || {}).forEach(([empCode, days]) => {
    dates.forEach((d, i) => {
      const date = toIsoFn(d);
      const dk = dayKeyFn(d, i);
      map[slotKey(empCode, date)] = canonicalSlotValue(days?.[dk]);
    });
  });
  return map;
}

export function mergeClassifiedSlots({ localMap, snapshotSlots, monitorSlots, monitorComplete = false }) {
  // Join SoftOne / snapshot / local by digit-normalized AFM so "0999…" and "999…"
  // (or punctuated PCODEs) land on the same schedule cell instead of SoftOne dashes.
  const localByDigits = {};
  Object.entries(localMap || {}).forEach(([key, val]) => {
    const { employee_code, date } = parseSlotKey(key);
    const dig = employeeCodeDigits(employee_code);
    if (!dig || !date) return;
    const dkey = `${dig}::${date}`;
    if (!localByDigits[dkey]) {
      localByDigits[dkey] = { key, code: employee_code, value: val };
    }
  });

  const snapByDigits = {};
  (snapshotSlots || []).forEach((s) => {
    const dig = employeeCodeDigits(s.employee_code);
    const date = String(s.date || "").slice(0, 10);
    if (!dig || !date) return;
    snapByDigits[`${dig}::${date}`] = s;
  });

  const monByDigits = {};
  (monitorSlots || []).forEach((s) => {
    const code = s.employee_code || s.employeeCode;
    const dig = employeeCodeDigits(code);
    const date = String(s.date || s.work_date || "").slice(0, 10);
    if (!dig || !date) return;
    monByDigits[`${dig}::${date}`] = s;
  });

  const digitKeys = new Set([
    ...Object.keys(localByDigits),
    ...Object.keys(snapByDigits),
    ...Object.keys(monByDigits),
  ]);

  const out = [];
  digitKeys.forEach((dkey) => {
    const sep = dkey.lastIndexOf("::");
    const dig = dkey.slice(0, sep);
    const date = dkey.slice(sep + 2);
    const localEntry = localByDigits[dkey];
    const snap = snapByDigits[dkey] || {};
    const mon = monByDigits[dkey];
    const monCode = mon?.employee_code || mon?.employeeCode;
    const employee_code =
      localEntry?.code
      || String(snap.employee_code || "").trim()
      || String(monCode || "").trim()
      || dig;
    const key = localEntry?.key || slotKey(employee_code, date);
    const local = canonicalSlotValue(localEntry?.value ?? localMap?.[key] ?? "");
    const submitted = canonicalSlotValue(snap.submitted ?? snap.submitted_value ?? "");
    const foxline = monitorComplete
      ? (mon ? foxlineMonitorRowToCanonical(mon) : "")
      : (mon
        ? foxlineMonitorRowToCanonical(mon)
        : canonicalSlotValue(snap.foxline ?? snap.foxline_value ?? ""));
    const classification = classifySlot({ submitted, foxline, local });
    out.push({
      key,
      employee_code,
      date,
      local,
      submitted,
      foxline,
      classification,
      submitted_at: snap.submitted_at || null,
      submitted_by: snap.submitted_by || null,
      foxline_fetched_at: mon ? (mon.fetched_at || null) : (snap.foxline_fetched_at || null),
      softone_id: String(mon?.softone_id || snap?.softone_id || "").trim() || null,
      action_audit: normalizeActionAudit(mon?.action_audit || mon?.actionAudit || snap?.action_audit) || null,
      sendable: isSoftoneSendableValue(local),
    });
  });
  out.sort((a, b) => (a.date || "").localeCompare(b.date || "") || (a.employee_code || "").localeCompare(b.employee_code || ""));
  return out;
}

export function groupSlotsByClass(slots) {
  const groups = {};
  Object.values(SLOT_CLASS).forEach((c) => {
    groups[c] = [];
  });
  (slots || []).forEach((slot) => {
    const c = slot.classification || SLOT_CLASS.IDLE;
    if (!groups[c]) groups[c] = [];
    groups[c].push(slot);
  });
  return groups;
}

export function weekHasFirstSend(slots, firstSentAt) {
  if (firstSentAt) return true;
  return (slots || []).some((s) => !isEmptySlot(s.submitted));
}

export function preexistingOccupiedSlots(slots) {
  return (slots || []).filter((s) => s.classification === SLOT_CLASS.PREEXISTING && !isEmptySlot(s.foxline));
}

export function actionableSlotClasses() {
  return [
    SLOT_CLASS.CONFLICT,
    SLOT_CLASS.FOXLINE_CHANGED,
    SLOT_CLASS.DELETED_IN_SOFTONE,
    SLOT_CLASS.API_ELIGIBLE,
    SLOT_CLASS.MANUAL_CHANGE,
    SLOT_CLASS.LOCAL_MATCHES_FOXLINE,
    SLOT_CLASS.PREEXISTING,
    SLOT_CLASS.IN_SYNC,
  ];
}

export function slotNeedsMarker(classification) {
  return Boolean(classification) && classification !== SLOT_CLASS.IDLE;
}

/** Check when Internal and SoftOne hold the same value, including both empty. */
export function softoneAgreesWithInternal(internalValue, foxlineValue) {
  return canonicalSlotValue(internalValue) === canonicalSlotValue(foxlineValue);
}

/** True when live Foxline currently holds this slot (sent by us or already there). */
export function slotIsInFoxline(classification) {
  return (
    classification === SLOT_CLASS.IN_SYNC ||
    classification === SLOT_CLASS.PREEXISTING ||
    classification === SLOT_CLASS.LOCAL_MATCHES_FOXLINE
  );
}

/** Local vs Foxline drift that must not look like a blank/normal cell. */
export function slotIsFoxlineDriftAlert(classification) {
  return (
    classification === SLOT_CLASS.MANUAL_CHANGE ||
    classification === SLOT_CLASS.CONFLICT ||
    classification === SLOT_CLASS.FOXLINE_CHANGED ||
    classification === SLOT_CLASS.DELETED_IN_SOFTONE
  );
}

/** One employee's work-monitor rows, keyed for the crew schedule viewer. */
export function ownSoftoneSlotsByKey(rows, employeeCode) {
  const map = {};
  const fallback = String(employeeCode || "").trim();
  (rows || []).forEach((row) => {
    const date = String(row?.date || row?.work_date || "").slice(0, 10);
    const code = String(row?.employee_code || row?.employeeCode || fallback).trim();
    if (!code || !date) return;
    const foxline = foxlineMonitorRowToCanonical(row);
    const key = slotKey(code, date);
    const slot = {
      key,
      employee_code: code,
      date,
      foxline,
      local: "",
      softone_id: String(row?.softone_id || "").trim() || null,
      classification: foxline ? SLOT_CLASS.PREEXISTING : SLOT_CLASS.IDLE,
    };
    map[key] = slot;
    const sessionKey = slotKey(fallback, date);
    if (fallback && sessionKey !== key && code.replace(/\D/g, "") === fallback.replace(/\D/g, "")) {
      map[sessionKey] = slot;
    }
  });
  return map;
}

/** True when the crew week has a published shift or a SoftOne shift to show. */
export function crewWeekHasShift(dates, internalByDate, softoneByKey, employeeCode) {
  return (dates || []).some((date) => {
    const internal = internalByDate?.[date] || "";
    const fox = softoneByKey?.[slotKey(employeeCode, date)];
    return Boolean(canonicalSlotValue(internal) || softoneValueWhenInternalBlank(fox, internal));
  });
}

/**
 * Visible SoftOne fill when Internal is blank, or when SoftOne owns the day
 * without an action id (locked — Internal should be cleared / ignored).
 * Does not write Internal by itself.
 */
export function softoneValueWhenInternalBlank(slot, internalValue) {
  if (!slot) return "";
  const fox = canonicalSlotValue(slot.foxline);
  if (!fox) return "";
  if (slotSoftoneChangeLocked(slot)) return fox;
  const local = canonicalSlotValue(internalValue !== undefined ? internalValue : slot.local);
  if (local) return "";
  return fox;
}

/**
 * Value schedule rules use. SoftOne-locked cells prefer SoftOne.
 * Otherwise Internal wins when set; blank Internal uses SoftOne fill.
 */
export function displayedScheduleValue(internalValue, foxSlot) {
  if (slotSoftoneChangeLocked(foxSlot)) {
    return softoneValueWhenInternalBlank(foxSlot, internalValue) || "";
  }
  const local = canonicalSlotValue(internalValue);
  if (local) return local;
  return softoneValueWhenInternalBlank(foxSlot, internalValue) || "";
}

export const PEOPLE_COST_EFKA = 1.2415;

/** Paid hours in a canonical shift. OFF, leave, and blanks are 0. */
export function labourHoursFromSlotValue(value) {
  const canonical = canonicalSlotValue(value);
  const match = canonical.match(/^(\d{2}):(\d{2})-(\d{2}):(\d{2})$/);
  if (!match) return 0;
  const start = Number(match[1]) * 60 + Number(match[2]);
  let end = Number(match[3]) * 60 + Number(match[4]);
  if (end < start) end += 24 * 60;
  return (end - start) / 60;
}

/**
 * Hours the grid is showing: SoftOne when locked or Internal blank; else Internal.
 * Returns source "softone" only for hours that SoftOne fill owns.
 */
export function displayedLabourHours(internalValue, foxSlot) {
  if (slotSoftoneChangeLocked(foxSlot)) {
    const foxHours = labourHoursFromSlotValue(softoneValueWhenInternalBlank(foxSlot, internalValue));
    if (foxHours > 0) return { hours: foxHours, source: "softone" };
    return { hours: 0, source: null };
  }
  const internalHours = labourHoursFromSlotValue(internalValue);
  if (internalHours > 0) return { hours: internalHours, source: "internal" };
  const foxHours = labourHoursFromSlotValue(softoneValueWhenInternalBlank(foxSlot, internalValue));
  if (foxHours > 0) return { hours: foxHours, source: "softone" };
  return { hours: 0, source: null };
}

/**
 * People-cost % for a day. Internal labour stays on the API figure (per-person rates).
 * SoftOne hours shown in blank cells are added at the store average rate.
 */
export function peopleCostPctWithDisplayedSlots({
  apiLabourCostEur,
  apiPeopleCostPct,
  revenue,
  softoneOnlyHours,
  avgHourlyRate,
  efka = PEOPLE_COST_EFKA,
} = {}) {
  const rev = Number(revenue);
  const extraHours = Number(softoneOnlyHours) || 0;
  const rate = Number(avgHourlyRate);
  const base = Number(apiLabourCostEur);
  const baseCost = Number.isFinite(base) && base > 0 ? base : 0;
  if (extraHours > 0 && Number.isFinite(rev) && rev > 0 && Number.isFinite(rate) && rate > 0) {
    return ((baseCost + extraHours * rate * efka) / rev) * 100;
  }
  if (baseCost <= 0) return null;
  const pct = Number(apiPeopleCostPct);
  return Number.isFinite(pct) ? pct : null;
}

/**
 * How a day's people cost moves if scheduled people take leave.
 * Shift costs are shares of labourCostEur, split by the caller's cost weights.
 * leavesNeeded is the fewest removable shifts, largest first, that land at or under thresholdPct.
 */
export function dayOffPeopleCostPlan({
  revenue,
  labourCostEur,
  thresholdPct,
  shifts,
} = {}) {
  const rev = Number(revenue);
  const labour = Number(labourCostEur);
  const threshold = Number(thresholdPct);
  if (!Number.isFinite(rev) || rev <= 0 || !Number.isFinite(labour) || labour < 0 || !Number.isFinite(threshold)) {
    return {
      currentPct: null,
      leavesNeeded: null,
      reachable: false,
      afterAllPct: null,
      pctIfOnly: {},
      scaledCostByCode: {},
      labourCostEur: null,
      revenue: null,
    };
  }
  const currentPct = (labour / rev) * 100;
  const usable = (shifts || [])
    .map((shift) => ({
      code: String(shift?.code || "").trim(),
      costEur: Number(shift?.costEur),
      removable: Boolean(shift?.removable),
    }))
    .filter((shift) => shift.code && Number.isFinite(shift.costEur) && shift.costEur > 0);
  const estimatedTotal = usable.reduce((sum, shift) => sum + shift.costEur, 0);
  const scale = estimatedTotal > 0 ? labour / estimatedTotal : 0;
  const ranked = usable
    .map((shift) => ({ ...shift, costEur: shift.costEur * scale }))
    .sort((a, b) => b.costEur - a.costEur || a.code.localeCompare(b.code));
  const pctIfOnly = {};
  const scaledCostByCode = {};
  for (const shift of ranked) {
    scaledCostByCode[shift.code] = shift.costEur;
    if (shift.removable) {
      pctIfOnly[shift.code] = ((labour - shift.costEur) / rev) * 100;
    }
  }
  const removable = ranked.filter((shift) => shift.removable);
  const afterAllRemoved = removable.reduce((sum, shift) => sum + shift.costEur, 0);
  const afterAllPct = ((labour - afterAllRemoved) / rev) * 100;
  if (currentPct <= threshold) {
    return {
      currentPct,
      leavesNeeded: 0,
      reachable: true,
      afterAllPct: currentPct,
      pctIfOnly,
      scaledCostByCode,
      labourCostEur: labour,
      revenue: rev,
    };
  }
  let removed = 0;
  let count = 0;
  for (const shift of removable) {
    removed += shift.costEur;
    count += 1;
    if (((labour - removed) / rev) * 100 <= threshold) {
      return {
        currentPct,
        leavesNeeded: count,
        reachable: true,
        afterAllPct,
        pctIfOnly,
        scaledCostByCode,
        labourCostEur: labour,
        revenue: rev,
      };
    }
  }
  return {
    currentPct,
    leavesNeeded: count,
    reachable: false,
    afterAllPct,
    pctIfOnly,
    scaledCostByCode,
    labourCostEur: labour,
    revenue: rev,
  };
}

export function foxlineGridHint(slot, lang) {
  if (!slot) return "";
  if (slotSoftoneChangeLocked(slot)) {
    return softoneChangeLockLabel(lang);
  }
  if (!slotNeedsMarker(slot.classification)) return "";
  const fox = displaySlotValue(slot.foxline, lang);
  const loc = displaySlotValue(slot.local, lang);
  const gr = lang === "gr";
  if (slot.classification === SLOT_CLASS.IN_SYNC) {
    return gr ? `Στο SoftOne: ${fox}` : `In SoftOne: ${fox}`;
  }
  if (slot.classification === SLOT_CLASS.API_ELIGIBLE) {
    return gr ? "Δεν στάλθηκε στο SoftOne" : "Not sent to SoftOne";
  }
  if (slot.classification === SLOT_CLASS.PREEXISTING) {
    return gr ? `Ήδη στο SoftOne: ${fox}` : `Already in SoftOne: ${fox}`;
  }
  if (slot.classification === SLOT_CLASS.MANUAL_CHANGE) {
    return gr
      ? `Εσωτερικό ${loc} · SoftOne ${fox}`
      : `Internal ${loc} · SoftOne ${fox}`;
  }
  return slotClassLabel(slot.classification, lang);
}

export function slotKeysForHistoryRow(row, classifiedSlots = []) {
  const keys = new Set();
  const date = String(row?.rota_date || row?.date || "").slice(0, 10);
  const code = String(row?.employee_code || "").trim();
  if (code && date) keys.add(slotKey(code, date));
  const day = String(row?.day_key || (date ? isoToDayKey3(date) : "") || "")
    .toLowerCase()
    .slice(0, 3);
  const empId = row?.employee_id != null && row.employee_id !== "" ? String(row.employee_id) : "";
  const name = String(row?.employee_name || "").trim().toLowerCase();
  (classifiedSlots || []).forEach((s) => {
    const sCode = String(s.employee_code || "").trim();
    const sId = s.employee_id != null && s.employee_id !== "" ? String(s.employee_id) : "";
    const sName = String(s.employee_name || "").trim().toLowerCase();
    const personMatch =
      (code && sCode === code) || (empId && sId === empId) || (name && sName === name);
    const whenMatch = (date && s.date === date) || (day && isoToDayKey3(s.date) === day);
    if (personMatch && whenMatch) keys.add(s.key || slotKey(sCode, s.date));
  });
  return [...keys];
}

export function historyRowDedupeKey(row, slotKeys = []) {
  return [
    slotKeys.slice().sort().join(","),
    String(row?.change_type || row?.event_type || "").toLowerCase(),
    String(row?.old_value ?? ""),
    String(row?.new_value ?? ""),
    String(row?.source || ""),
    String(row?.changed_at || "").slice(0, 19),
  ].join("|");
}

export function indexHistoryBySlot(rows, classifiedSlots = []) {
  const map = {};
  const seen = new Set();
  (rows || []).forEach((row) => {
    const keys = slotKeysForHistoryRow(row, classifiedSlots);
    if (!keys.length) return;
    const dedupe = historyRowDedupeKey(row, keys);
    if (seen.has(dedupe)) return;
    seen.add(dedupe);
    keys.forEach((k) => {
      if (!map[k]) map[k] = [];
      map[k].push(row);
    });
  });
  Object.keys(map).forEach((k) => {
    map[k].sort((a, b) => String(b.changed_at || "").localeCompare(String(a.changed_at || "")));
  });
  return map;
}

/** All change-log / snapshot rows for one employee + date, including draft local edits. */
export function historyRowsForSlot(rows, employeeCode, dateISO, extra = {}) {
  const code = String(employeeCode || "").trim();
  const date = String(dateISO || "").slice(0, 10);
  if (!code || !date) return [];
  const targetKey = slotKey(code, date);
  const stubSlots = [
    {
      key: targetKey,
      employee_code: code,
      employee_id: extra.employeeId,
      employee_name: extra.employeeName,
      date,
    },
  ];
  const map = indexHistoryBySlot(rows, stubSlots);
  return map[targetKey] || [];
}

export function filledWorkMonitorSlots(slots) {
  return (slots || []).filter((s) => !isEmptySlot(foxlineMonitorRowToCanonical(s)));
}

function softoneWritePayload(result) {
  const data = result?.data;
  if (data && typeof data === "object" && ("submitted" in data || "failed" in data || "errors" in data || "results" in data)) {
    return data;
  }
  if (result && typeof result === "object" && ("submitted" in result || "failed" in result || "errors" in result || "results" in result)) {
    return result;
  }
  return null;
}

function softoneWriteErrorText(entry) {
  if (!entry) return "";
  if (typeof entry === "string") return entry;
  return String(entry.error || entry.message || "");
}

function softoneWriteMissingActionId(text) {
  return /document KEY|no prior sync log|action id/i.test(String(text || ""));
}

/**
 * User-facing result of a SoftOne slot-sync write.
 * HTTP 200 can still mean every slot failed; this is the message Ops should show.
 * Returns null for reads (work-monitor refresh) that are not a write result.
 */
export function formatSoftoneWriteNotice(result, lang) {
  const payload = softoneWritePayload(result);
  if (!payload) return null;
  const gr = lang === "gr";
  const submitted = Number(payload.submitted) || 0;
  const failed = Number(payload.failed) || 0;
  const skipped = Number(payload.skipped) || 0;
  const texts = [
    ...(Array.isArray(payload.errors) ? payload.errors : []).map(softoneWriteErrorText),
    ...(Array.isArray(payload.results) ? payload.results : [])
      .filter((row) => row && row.ok === false && !row.skipped)
      .map((row) => softoneWriteErrorText(row)),
  ].filter(Boolean);
  const failCount = Math.max(failed, texts.length);
  const missingActionId = texts.some(softoneWriteMissingActionId);

  if (failCount > 0 && missingActionId) {
    const updated = submitted > 0
      ? (gr
        ? `Το SoftOne ενημερώθηκε για ${submitted} βάρδιες. `
        : `SoftOne updated ${submitted} slot(s). `)
      : "";
    return {
      tone: "danger",
      message: gr
        ? `${updated}Το SoftOne δεν ενημερώθηκε για ${failCount} βάρδιες. Υπάρχουν ήδη στο SoftOne και το Ops δεν έχει action id για να τις αντικαταστήσει. Ακυρώστε τις στο SoftOne και στείλτε ξανά.`
        : `${updated}SoftOne was not updated for ${failCount} slot(s). They already exist in SoftOne, and Ops has no action id to replace them. Cancel those shifts in SoftOne, then send again.`,
    };
  }

  if (failCount > 0) {
    const detail = texts[0] ? ` ${texts[0]}` : "";
    return {
      tone: "danger",
      message: gr
        ? `Το SoftOne δεν ενημερώθηκε για ${failCount} βάρδιες.${submitted > 0 ? ` ${submitted} στάλθηκαν.` : ""}${detail}`
        : `SoftOne was not updated for ${failCount} slot(s).${submitted > 0 ? ` ${submitted} were sent.` : ""}${detail}`,
    };
  }

  if (submitted > 0) {
    return {
      tone: "ok",
      message: gr
        ? `Το SoftOne ενημερώθηκε για ${submitted} βάρδιες.`
        : `SoftOne updated ${submitted} slot(s).`,
    };
  }

  if (skipped > 0) {
    return {
      tone: "danger",
      message: gr
        ? `Δεν στάλθηκε τίποτα στο SoftOne. ${skipped} βάρδιες παραλείφθηκαν γιατί το SoftOne έχει ήδη τιμή.`
        : `Nothing was sent to SoftOne. ${skipped} slot(s) were skipped because SoftOne already has a value.`,
    };
  }

  return null;
}

export function formatMonitorCacheAge(fetchedAt, lang, now = new Date()) {
  if (!fetchedAt) return "";
  const fetched = new Date(fetchedAt);
  if (Number.isNaN(fetched.getTime())) return "";
  const seconds = Math.max(0, Math.floor((now.getTime() - fetched.getTime()) / 1000));
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const gr = lang === "gr";
  if (minutes < 1) return gr ? "λιγότερο από 1 λεπτό" : "less than a minute";
  if (minutes === 1) return gr ? "1 λεπτό" : "1 minute";
  if (minutes < 60) return gr ? `${minutes} λεπτά` : `${minutes} minutes`;
  if (hours === 1) return gr ? "1 ώρα" : "1 hour";
  if (hours < 24) return gr ? `${hours} ώρες` : `${hours} hours`;
  if (days === 1) return gr ? "1 ημέρα" : "1 day";
  return gr ? `${days} ημέρες` : `${days} days`;
}

export function logEventDescription(row, lang) {
  const t = String(row.change_type || row.event_type || "").toLowerCase();
  const oldV = displaySlotValue(row.old_value, lang);
  const newV = displaySlotValue(row.new_value, lang);
  const gr = lang === "gr";
  switch (t) {
    case SLOT_LOG_EVENT.ASSIGNED:
    case "assign":
      return gr ? `Ανατέθηκε ${newV}` : `Assigned ${newV}`;
    case SLOT_LOG_EVENT.REMOVED:
    case "remove":
      return gr ? `Αφαιρέθηκε (${oldV})` : `Removed (${oldV})`;
    case SLOT_LOG_EVENT.MODIFIED:
    case "modify":
      return gr ? `Εσωτερικό ${oldV} → ${newV}` : `Internal ${oldV} → ${newV}`;
    case SLOT_LOG_EVENT.FIRST_SEND:
      return gr ? `Πρώτη αποστολή ${newV}` : `First send ${newV}`;
    case SLOT_LOG_EVENT.API_BLANK_UPLOAD:
      return gr ? `Αποστολή κενού slot ${newV}` : `Blank upload ${newV}`;
    case SLOT_LOG_EVENT.MARK_SUBMITTED:
      return gr ? `Σημειώθηκε ως καταχωρημένο ${newV}` : `Marked submitted ${newV}`;
    case SLOT_LOG_EVENT.RESTORE_VIA_API:
      return gr ? `Επαναφορά μέσω API ${newV}` : `Restored via API ${newV}`;
    case SLOT_LOG_EVENT.REPLACE_VIA_API:
      return gr ? `Επαναποστολή SoftOne ${oldV} → ${newV}` : `Resent SoftOne ${oldV} → ${newV}`;
    case SLOT_LOG_EVENT.ACCEPT_DELETION:
      return gr ? "Αποδοχή διαγραφής SoftOne" : "Accepted SoftOne deletion";
    case SLOT_LOG_EVENT.FOXLINE_CHANGED:
      return gr ? `SoftOne άλλαξε ${oldV} → ${newV}` : `SoftOne changed ${oldV} → ${newV}`;
    case SLOT_LOG_EVENT.FOXLINE_DELETED:
      return gr ? `SoftOne διαγράφηκε (ήταν ${oldV})` : `SoftOne deleted (was ${oldV})`;
    case SLOT_LOG_EVENT.UPLOAD_FAILED:
      return gr ? `Αποτυχία αποστολής ${newV}` : `Upload failed ${newV}`;
    default:
      return gr ? `${oldV} → ${newV}` : `${oldV} → ${newV}`;
  }
}
